/**
 * useStemExtraction — iter-251
 *
 * Extracts the entire stem-extraction pipeline that used to live inline
 * in `ELPMStudioDAW.jsx`:
 *
 *   • `handleStemExtract` — entry-point fired from the StemExtractorCard
 *     CTA + the empty-state batch-drop flow. Resolves the source
 *     channel (auto-fallback to the first channel with audio if the
 *     user-selected one is empty) and opens the routing-preview modal.
 *
 *   • `runStemExtraction` — actual SSE-stream + fal.ai pipeline fired
 *     after the routing modal is confirmed. Owns the tab-badge cancel
 *     handle, the SSE progress state, the iter-249 proxy-URL fan-out,
 *     and the iter-220 auto-scroll-to-stems UX.
 *
 *   • `handleCancelStems` — wired to the routing modal's cancel button
 *     and the global "Stop all" cancel surfaces.
 *
 * Extracting them shrinks the parent file by ~280 lines and gives the
 * stem-extraction logic a stable, testable surface. Behaviour is
 * intentionally byte-identical to the inline versions — this is a pure
 * cut/paste with NO logic changes.
 */
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { toastErrorWithHelp } from "@/lib/toastErrorWithHelp";
import { pushBadge, updateBadge, clearBadge, TAB_BADGE_COLORS } from "@/lib/tabBadge";
import {
  streamPollingExtraction,
  pollExtractionToCompletion,
  readInflightExtraction,
  clearInflightExtraction,
} from "@/components/daw/streamPollingExtraction";
import { padCh, clamp, TOTAL_CHANNELS } from "@/components/daw/dawHelpers";
import detectKey from "@/lib/detectKey";

export default function useStemExtraction({
  engine,
  selectedChannel,
  setSelectedChannel,
  setBusy,
  setMaxDur,
  setLoadedCount,
  setVisibleChannels,
}) {
  // iter-167 — routing-preview confirmation { sourceIdx, targets } or null.
  const [stemConfirm, setStemConfirm] = useState(null);
  // SSE progress payload — last event yielded by /api/stems/{extract,extract_url}.
  const [stemProgress, setStemProgress] = useState(null);
  // AbortController for the in-flight extraction.
  const stemAbortRef = useRef(null);
  // iter-252 — Snapshot of the most recent successful extraction so the
  // "Download stems pack" action can re-fetch the same URLs without
  // re-running fal.ai. Shape: { sourceIdx, songName, stems: [{name, url}] }
  // or null when nothing has been extracted yet (or the user cleared it).
  const [lastExtraction, setLastExtraction] = useState(null);

  const handleStemExtract = async () => {
    // iter-166 — Auto-fallback: if the user-selected channel is empty
    // but some other channel has audio, extract from that channel
    // instead of refusing. iter-167 — before kicking off the (slow +
    // paid) fal.ai upload, show a routing-preview modal so the user
    // sees EXACTLY which channels each stem will land on.
    let sourceIdx = selectedChannel;
    let sourceCh = engine.channels[sourceIdx];
    if (!sourceCh?.buffer) {
      // Find the first channel that DOES have audio.
      const firstWithAudio = engine.channels.findIndex((c) => c?.buffer);
      if (firstWithAudio === -1) {
        toast.error("No audio loaded on any channel", {
          description: "Upload an MP3 / WAV or record a track first, then click Extract Stems.",
          duration: 10000,
        });
        return;
      }
      sourceIdx = firstWithAudio;
      sourceCh = engine.channels[sourceIdx];
      toast.info(`Using CH ${String(sourceIdx + 1).padStart(2, "0")} as source`, {
        description: `The selected channel was empty, so I'm extracting from the first channel with audio. Click on the channel strip you want to extract from to override this.`,
        duration: 6000,
      });
    }
    // Pre-compute the routing — 6 stems from Demucs 6s.
    const targets = engine.freeChannelIndices?.(6) || [];
    setStemConfirm({ sourceIdx, targets });
  };

  // iter-167 — Actual extraction logic, fired after the routing-preview
  // modal is confirmed. iter-168 — accepts a user-edited routing array
  // (length 6, may contain nulls for stems the user chose to skip) so
  // each stem lands EXACTLY where the user requested. The mapping by
  // stem name uses the PREDICTED_STEMS order from the modal which
  // matches the order Demucs returns.
  const runStemExtraction = async (sourceIdx, userRouting = null) => {
    setStemConfirm(null);
    // iter-214 — Fast path: if this channel was loaded from a public URL
    // (Suno library, SongLibrary, SunoModal), call /api/stems/extract_url
    // and have fal.ai fetch directly. Skips the 60-100 MB WAV bounce +
    // upload step that's the slowest part of stem extraction (and the
    // most likely to trip proxy timeouts on long songs — see iter-213).
    const channelSourceUrl = engine.channels[sourceIdx]?.sourceUrl || null;
    const isFastPath = !!channelSourceUrl && /^https?:\/\//i.test(channelSourceUrl);
    let wav = null;
    if (!isFastPath) {
      wav = engine.channelToWavBlob(sourceIdx);
      if (!wav) { toast.error("Could not encode channel audio"); return; }
      // Pre-flight size check — bounce big payloads BEFORE the round-trip so
      // users get instant feedback instead of waiting for the server's 413.
      // Keep this in sync with backend/routes/stems.py::MAX_STEM_UPLOAD_BYTES.
      const STEM_MAX_BYTES = 200 * 1024 * 1024;
      if (wav.size > STEM_MAX_BYTES) {
        const mb = (wav.size / 1024 / 1024).toFixed(1);
        toast.error("Audio file too large for stem extraction", {
          description: `${mb} MB > 200 MB cap. Trim the track in the editor or export at a lower sample rate first.`,
        });
        return;
      }
    }

    setBusy("stems");
    setStemProgress({ status: isFastPath ? "submitted" : "uploading" });
    const controller = new AbortController();
    stemAbortRef.current = controller;
    // iter-111 — tab badge for stem extraction (typically 30-90 s,
    // user routinely tabs away while uploading + waiting on fal.ai).
    // Pass the AbortController so "Stop all" / per-task X actually
    // aborts the upload + closes the SSE connection.
    const stemBadgeId = pushBadge({
      label: isFastPath ? "Extracting stems (URL)" : "Extracting stems",
      color: TAB_BADGE_COLORS.composing,
      elapsed: 0,
      cancel: () => { try { controller.abort(); } catch (e) { /* noop */ } },
    });
    const stemBadgeStart = Date.now();
    const stemBadgeTicker = setInterval(() => {
      updateBadge(stemBadgeId, { elapsed: Math.floor((Date.now() - stemBadgeStart) / 1000) });
    }, 1000);
    let stemOk = false;

    try {
      // iter-260c — Three-step direct-to-fal flow:
      //   1. POST /fal_upload_init  → get fal.media v3 bearer token + upload URL
      //   2. POST WAV directly to fal.media (NO proxy between browser
      //      and fal CDN, so the upload duration is bounded only by
      //      uplink speed — no chance of a backend proxy total-
      //      duration cap killing the request)
      //   3. POST /extract_url with the resulting access_url → short SSE
      //
      // If step 1 OR step 2 fails (CORS, token endpoint down, etc.)
      // we fall back to the iter-260 /upload_temp path so a single
      // misconfigured environment doesn't break the feature entirely.
      const form = new FormData();
      form.append("model", "htdemucs_6s");
      if (isFastPath) {
        form.append("url", channelSourceUrl);
      } else {
        setStemProgress({ status: "uploading", phase: "to_fal_cdn" });
        let publicUrl = null;
        try {
          // Step 1 — get a short-lived bearer token + the v3 CDN URL.
          const initRes = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/stems/fal_upload_init`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              signal: controller.signal,
              body: JSON.stringify({
                file_name: `ch${padCh(sourceIdx)}.wav`,
                content_type: "audio/wav",
              }),
            },
          );
          if (!initRes.ok) {
            const txt = await initRes.text().catch(() => "");
            throw new Error(`fal_upload_init HTTP ${initRes.status}: ${txt.slice(0, 200)}`);
          }
          const init = await initRes.json();
          // Step 2 — POST directly to fal.media (no proxy in the middle).
          const cdnRes = await fetch(init.upload_url, {
            method: "POST",
            headers: {
              "Authorization": `${init.token_type} ${init.token}`,
              "Content-Type": "audio/wav",
              "X-Fal-File-Name": init.file_name,
            },
            body: wav,
            signal: controller.signal,
          });
          if (!cdnRes.ok) {
            const txt = await cdnRes.text().catch(() => "");
            throw new Error(`Direct fal CDN upload failed (HTTP ${cdnRes.status}): ${txt.slice(0, 200)}`);
          }
          const cdnJson = await cdnRes.json();
          if (!cdnJson?.access_url) {
            throw new Error(`fal CDN upload returned no access_url: ${JSON.stringify(cdnJson).slice(0, 200)}`);
          }
          publicUrl = cdnJson.access_url;
        } catch (directErr) {
          if (directErr.name === "AbortError") throw directErr;
          // Fallback — re-upload to OUR /upload_temp (iter-260b's
          // server-side fal.storage re-upload kicks in inside the
          // SSE stream).
          // eslint-disable-next-line no-console
          console.warn("Direct fal CDN upload failed, falling back to /upload_temp:", directErr.message);
          setStemProgress({ status: "uploading", phase: "to_backend_fallback" });
          const uploadForm = new FormData();
          uploadForm.append("audio", wav, `ch${padCh(sourceIdx)}.wav`);
          const uploadRes = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/stems/upload_temp`,
            { method: "POST", body: uploadForm, signal: controller.signal },
          );
          if (!uploadRes.ok) {
            const txt = await uploadRes.text().catch(() => "");
            let detail = txt;
            try { detail = JSON.parse(txt).detail || txt; } catch (e) { void e; }
            const err = new Error(`Pre-upload failed (HTTP ${uploadRes.status}): ${String(detail).slice(0, 200)}`);
            err.status = uploadRes.status;
            throw err;
          }
          const uploadJson = await uploadRes.json();
          publicUrl = `${process.env.REACT_APP_BACKEND_URL}${uploadJson.path}`;
        }
        // Step 3 — kick off extraction against the URL fal can definitely fetch.
        form.append("url", publicUrl);
        setStemProgress({ status: "submitted", phase: "fal_processing" });
      }

      // iter-264 — Polling pipeline (replaces the iter-260c SSE call).
      // RCA: nginx-ingress proxy-read-timeout (60 s) does not reset on
      // SSE comment lines, so any extraction taking >60 s would lose
      // its connection mid-stream. The polling client splits the same
      // workflow into ~30 short requests (<1 s each) so no proxy
      // timeout can ever bite us. Same callable surface as the old
      // streamMultipartSse — the rest of this function is unchanged.
      const finalEvent = await streamPollingExtraction({
        url: `${process.env.REACT_APP_BACKEND_URL}/api/stems/extract_async`,
        formData: form,
        signal: controller.signal,
        onEvent: (ev) => setStemProgress(ev),
      });

      // iter-169 — Surface the real backend error to the user instead of
      // a generic "No stems returned" message. The backend may yield:
      //   {"status": "done", "stems": {...}}             — success
      //   {"error": "fal.ai returned no stems", ...}     — parsing failed
      //   {"error": "fal.ai balance exhausted", ...}     — billing
      //   {"error": "Stem extraction failed: ...", ...}  — generic failure
      // Pre-iter-169 we ignored finalEvent.error and threw a generic
      // "No stems returned" so users had no way to know if it was a
      // balance / quota / fal-side issue. Now we extract the actual
      // error string and let the outer catch route it to the right
      // friendly toast (balance, rate limit, too large, etc).
      if (finalEvent?.error) {
        const err = new Error(finalEvent.error);
        if (finalEvent.code) err.code = finalEvent.code;
        throw err;
      }
      if (!finalEvent) {
        throw new Error("Connection to fal.ai dropped before stems were returned. Try again — if it keeps failing, check your network and fal.ai/dashboard for the request status.");
      }
      if (finalEvent.status && finalEvent.status !== "done") {
        // Stream ended on a non-done event. With the iter-264 polling
        // pipeline this should be essentially impossible (the helper
        // throws on `error` and only returns on `done`) — kept here
        // as a defensive fallback in case the backend's status payload
        // shape evolves.
        // iter-264 — Removed the iter-213 "≤3 min" hint because the
        // polling pipeline is bounded only by the 12-min poll cap, not
        // by any proxy timeout. The user's clip length is no longer
        // the relevant variable.
        const lastStatus = finalEvent.status;
        let hint;
        if (lastStatus === "uploading") {
          hint = "The audio upload to fal.ai never finished. Check your network connection.";
        } else if (lastStatus === "submitted" || lastStatus === "queued") {
          hint = "fal.ai accepted the job but didn't return stems in time. fal.ai may be overloaded — try again in a minute.";
        } else {
          hint = "fal.ai is taking unusually long to finish. Try again in a few minutes — your last request_id is " + (finalEvent.request_id || "unknown") + ".";
        }
        throw new Error(`fal.ai stopped at status "${lastStatus}" without returning stems. ${hint}`);
      }
      if (!finalEvent.stems || Object.keys(finalEvent.stems).length === 0) {
        throw new Error("fal.ai responded with an empty stem set. Try the htdemucs_6s model on a longer clip, or check fal.ai/dashboard for what happened.");
      }

      // iter-168 — Honour the user's per-stem destination override from
      // the routing-preview modal. The stems Demucs returns are keyed by
      // name ("vocals", "drums", "bass", "other", "guitar", "piano");
      // userRouting is a 6-element array indexed by PREDICTED_STEMS order.
      // Stems set to `null` (user picked "skip") are dropped entirely.
      const STEM_ORDER = ["vocals", "drums", "bass", "other", "guitar", "piano"];
      const stemEntries = [];
      if (userRouting && Array.isArray(userRouting)) {
        STEM_ORDER.forEach((stemName, i) => {
          const dest = userRouting[i];
          const url = finalEvent.stems[stemName];
          if (dest != null && url) stemEntries.push([stemName, url, dest]);
        });
      } else {
        // Fallback path: auto-find free channels (preserves old behaviour
        // if anyone calls runStemExtraction without a routing array).
        const auto = engine.freeChannelIndices(Object.keys(finalEvent.stems).length);
        Object.entries(finalEvent.stems).forEach(([stemName, url], i) => {
          if (auto[i] != null) stemEntries.push([stemName, url, auto[i]]);
        });
      }

      if (stemEntries.length === 0) {
        toast.error("Nothing to load", { description: "Every stem was set to skip." });
        return;
      }

      const loaded = [];
      const perStemErrors = [];
      for (let i = 0; i < stemEntries.length; i++) {
        const [stemName, url, targetIdx] = stemEntries[i];
        setStemProgress({ status: "downloading", stem: stemName, index: i + 1, total: stemEntries.length });
        // iter-249 — Always route fal.media stem URLs through our backend
        // proxy. fal.media's CORS policy + corporate firewalls were the
        // root cause of "extraction succeeds but no stems appear" reports
        // — the browser fetch failed for all 6 URLs silently. The proxy
        // makes the fetch same-origin so it can never be CORS-blocked.
        const proxiedUrl = `${process.env.REACT_APP_BACKEND_URL}/api/stems/proxy?url=${encodeURIComponent(url)}`;
        try {
          await engine.loadUrlToChannel(targetIdx, proxiedUrl, `${stemName}-ch${padCh(sourceIdx)}.mp3`);
          loaded.push({ name: stemName, channel: targetIdx });
        } catch (eProxied) {
          // Fall back to a direct fetch in case the proxy itself failed
          // (e.g. fal.media host wasn't whitelisted, transient backend
          // issue). Capture both errors so the toast tells the truth.
          try {
            await engine.loadUrlToChannel(targetIdx, url, `${stemName}-ch${padCh(sourceIdx)}.mp3`);
            loaded.push({ name: stemName, channel: targetIdx });
          } catch (eDirect) {
            const msg = `proxy: ${eProxied.message || "?"} · direct: ${eDirect.message || "?"}`;
            perStemErrors.push({ stem: stemName, error: msg });
            toast.error(`Could not load ${stemName}`, { description: msg });
          }
        }
      }

      // iter-249 — Bail loudly when EVERY stem failed. Pre-iter-249 we
      // still rendered the "Extracted 0 stems" success toast which made
      // the failure look like a no-op. Now the user sees the actual
      // problem so they can take action (toggle network, top up fal
      // balance, retry, etc.).
      if (loaded.length === 0 && stemEntries.length > 0) {
        const summary = perStemErrors.map((p) => `${p.stem}: ${p.error}`).join("\n");
        throw new Error(
          `Stems were returned but none could be downloaded into the mixer.\n\n${summary}\n\nThis usually means fal.media's mp3 URLs aren't reachable from your browser. Check the browser console + your network, then try again.`,
        );
      }

      setMaxDur(engine.maxDuration);
      setLoadedCount((c) => c + loaded.length);
      // iter-220 — Auto-scroll to where the stems landed. On a 64-channel
      // mixer, the next 6 free channels are almost always BELOW the
      // visible fold (typically out of sight if the source was CH 01),
      // which is exactly the "extraction completed but no stems appear"
      // bug the user reported. We expand the visible-channel window AND
      // dispatch the existing `elpm:jump-to-channel` event so the first
      // stem strip is brought into view + selected.
      const firstStemCh = loaded[0]?.channel ?? sourceIdx;
      const lastStemCh = loaded.length
        ? Math.max(...loaded.map((l) => l.channel))
        : sourceIdx;
      setSelectedChannel(firstStemCh);
      setVisibleChannels((cur) => clamp(Math.max(cur, lastStemCh + 1), 8, TOTAL_CHANNELS));
      requestAnimationFrame(() => {
        try {
          window.dispatchEvent(new CustomEvent("elpm:jump-to-channel", { detail: { ch: firstStemCh } }));
        } catch (e) { void e; }
      });
      toast.success(`Extracted ${loaded.length} stems${isFastPath ? " · fast path" : ""}`, {
        description: (isFastPath ? "Skipped the WAV upload (loaded from URL) · " : "") +
          loaded.map((l) => `${l.name}→CH${padCh(l.channel)}`).join(" · "),
        action: {
          label: `Jump to CH ${padCh(firstStemCh)}`,
          onClick: () => {
            try {
              window.dispatchEvent(new CustomEvent("elpm:jump-to-channel", { detail: { ch: firstStemCh } }));
            } catch (e) { void e; }
          },
        },
        duration: 10000,
      });
      // iter-252 — Snapshot the URLs so the user can re-pack them into a
      // .zip without re-running fal.ai. Then surface a follow-up toast
      // with the download action so the affordance is impossible to miss.
      const stemSnapshot = stemEntries
        .filter(([name]) => loaded.some((l) => l.name === name))
        .map(([name, url]) => ({ name, url }));
      const songName = (engine.channels[sourceIdx]?.name || `CH${padCh(sourceIdx)}`)
        .replace(/\.[^.]+$/, "")
        .replace(/[·•]+/g, "-");
      // iter-253 — Build the auto-tagged manifest. Key detection runs on
      // the source channel's buffer (NOT the extracted stems) because:
      //   • The source mix has all the harmonic content the chromagram
      //     needs to do a confident estimate.
      //   • It's already in the AudioContext — no extra fetch.
      //   • The detected key is the same for the whole pack (stems share
      //     the source's tonality), so doing it once is correct + cheap.
      // Wrapped in try/catch so a key-detection error never blocks the
      // download — manifest just omits `key` in that case.
      let detectedKey = null;
      try {
        const srcBuf = engine.channels[sourceIdx]?.buffer;
        if (srcBuf) detectedKey = detectKey(srcBuf);
      } catch (keyErr) {
        // Soft fail — manifest is best-effort.
        // eslint-disable-next-line no-console
        console.warn("detectKey failed:", keyErr);
      }
      const manifest = {
        song_name: songName,
        source_url: channelSourceUrl || null,
        source_type: isFastPath ? "url" : "user_upload",
        tempo_bpm: typeof engine.bpm === "number" ? Math.round(engine.bpm * 100) / 100 : null,
        key: detectedKey?.key || null,
        key_root: detectedKey?.root || null,
        key_quality: detectedKey?.quality || null,
        key_confidence: detectedKey
          ? Math.round(detectedKey.confidence * 1000) / 1000
          : null,
        stems_model: "htdemucs_6s",
        stems_count: stemSnapshot.length,
        generated_at: new Date().toISOString(),
        tool: "ELPM Studio Pro",
      };
      setLastExtraction({ sourceIdx, songName, stems: stemSnapshot, manifest });
      // Defer the follow-up toast so it lands AFTER the main success
      // toast so the user sees both — sonner stacks them top-to-bottom.
      setTimeout(() => {
        const keyHint = detectedKey ? ` · key ${detectedKey.key}` : "";
        toast.message(`Take the ${stemSnapshot.length} stems out of the browser`, {
          description: `One-click download — packs ${stemSnapshot.length} stems${keyHint} into a .zip with a manifest.json.`,
          action: {
            label: "Download .zip",
            onClick: () => { downloadStemPack({ stems: stemSnapshot, songName, manifest }); },
          },
          duration: 15000,
        });
      }, 1200);
      stemOk = loaded.length > 0;
    } catch (e) {
      if (e.name === "AbortError") {
        toast.message("Stem extraction cancelled");
      } else if (e.message?.includes("balance exhausted") || e.message?.includes("fal_balance_exhausted")) {
        toast.error("fal.ai balance exhausted", { description: "Top up at fal.ai/dashboard/billing to continue." });
      } else if (e.status === 413 || e.message?.toLowerCase().includes("too large") || e.message?.toLowerCase().includes("payload too large")) {
        toast.error("Audio too large for stem extraction", {
          description: "Trim the clip in the Audio Editor, lower the sample rate on export, or split the song into shorter sections (max 200 MB).",
        });
      } else if (e.status === 429) {
        toast.error("Slow down — rate limit hit", { description: e.message });
      } else {
        toastErrorWithHelp("Stem extraction failed", {
          description: e.message,
          probe: "backend",
        });
      }
    } finally {
      // Clear stem-extract tab badge — flash green if stems landed.
      clearInterval(stemBadgeTicker);
      clearBadge(stemBadgeId, stemOk, "Stems ready");
      setBusy(null);
      setStemProgress(null);
      stemAbortRef.current = null;
    }
  };

  const handleCancelStems = () => {
    stemAbortRef.current?.abort();
  };

  /**
   * iter-252 — Pack the last extracted stems into a .zip and trigger a
   * browser download. Defaults to the last extraction's URLs + song
   * name; callers can override by passing `{ stems, songName }`.
   *
   * Defensive: if no extraction has happened in this session, surfaces
   * a friendly hint instead of POSTing an empty body.
   */
  const downloadStemPack = async (override = null) => {
    const payload = override || lastExtraction;
    if (!payload || !payload.stems || payload.stems.length === 0) {
      toast.message("No stems to pack yet", {
        description: "Run a stem extraction first — the pack action will appear right after.",
      });
      return;
    }
    const songName = (payload.songName || "stems").trim() || "stems";
    const packToastId = toast.loading(`Packing ${payload.stems.length} stems into .zip…`, {
      description: "Pulling from the proxy cache — second pack is instant.",
    });
    try {
      const body = { stems: payload.stems, song_name: songName };
      // iter-253 — pass the manifest alongside so the backend can embed
      // it as `manifest.json` at the top of the zip.
      if (payload.manifest) body.manifest = payload.manifest;
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stems/pack`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        let detail = text;
        try { detail = JSON.parse(text).detail || text; } catch (e) { void e; }
        throw new Error(`Pack failed (${res.status}): ${detail || res.statusText}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${songName}-stems.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Defer revoke so Safari has time to actually start the download.
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.dismiss(packToastId);
      toast.success(`Downloaded ${payload.stems.length}-stem pack`, {
        description: `${songName}-stems.zip is in your Downloads folder.`,
        duration: 6000,
      });
    } catch (e) {
      toast.dismiss(packToastId);
      toastErrorWithHelp("Could not pack stems", {
        description: e.message,
        probe: "backend",
      });
    }
  };

  // ── iter-264 — Resume an in-flight stem extraction on page load ────
  // When the user refreshes (or accidentally closes + reopens the tab)
  // while a fal.ai job is processing, the request_id is still valid for
  // ~10 min on fal's side. We persist it to localStorage in
  // `streamPollingExtraction` and pick it up here so the user doesn't
  // have to re-submit (and re-PAY for) the same extraction.
  //
  // Limitations vs. the fresh-extraction flow:
  //  • The original sourceIdx + userRouting were NOT persisted, so we
  //    fall back to auto-routing (first free channels). User can re-
  //    organise after.
  //  • If the engine isn't ready yet (rare — DAW initialises engine
  //    synchronously on first render), we wait until it is.
  const resumeAttemptedRef = useRef(false);
  const runResumedExtraction = async (inflight) => {
    setBusy("stems");
    setStemProgress({ status: "resuming", request_id: inflight.requestId });
    const controller = new AbortController();
    stemAbortRef.current = controller;
    const stemBadgeId = pushBadge({
      label: "Resuming stems",
      color: TAB_BADGE_COLORS.composing,
      elapsed: 0,
      cancel: () => { try { controller.abort(); } catch (e) { void e; } },
    });
    const stemBadgeStart = Date.now();
    const stemBadgeTicker = setInterval(() => {
      updateBadge(stemBadgeId, { elapsed: Math.floor((Date.now() - stemBadgeStart) / 1000) });
    }, 1000);
    const shortId = (inflight.requestId || "").slice(0, 8);
    const resumeToastId = toast.loading(`Resuming previous stem extraction…`, {
      description: `Job ${shortId}… — picking up where you left off, no re-charge to fal.ai.`,
    });
    let stemOk = false;
    try {
      const finalEvent = await pollExtractionToCompletion({
        apiBase: inflight.apiBase || process.env.REACT_APP_BACKEND_URL,
        requestId: inflight.requestId,
        signal: controller.signal,
        onEvent: (ev) => setStemProgress(ev),
      });
      if (!finalEvent?.stems || Object.keys(finalEvent.stems).length === 0) {
        throw new Error("Resume completed but no stems were returned — try a fresh extraction.");
      }
      // Auto-route to free channels (original userRouting was lost on refresh).
      const stemNames = Object.keys(finalEvent.stems);
      const auto = engine.freeChannelIndices(stemNames.length);
      const loaded = [];
      for (let i = 0; i < stemNames.length; i++) {
        const stemName = stemNames[i];
        const url = finalEvent.stems[stemName];
        const targetIdx = auto[i];
        if (targetIdx == null) break;
        setStemProgress({ status: "downloading", stem: stemName, index: i + 1, total: stemNames.length });
        const proxiedUrl = `${process.env.REACT_APP_BACKEND_URL}/api/stems/proxy?url=${encodeURIComponent(url)}`;
        try {
          await engine.loadUrlToChannel(targetIdx, proxiedUrl, `${stemName}-resumed.mp3`);
          loaded.push({ name: stemName, channel: targetIdx });
        } catch (eProxied) {
          try {
            await engine.loadUrlToChannel(targetIdx, url, `${stemName}-resumed.mp3`);
            loaded.push({ name: stemName, channel: targetIdx });
          } catch (eDirect) {
            void eProxied; void eDirect; /* silent — fresh extraction will report */
          }
        }
      }
      if (loaded.length === 0) {
        throw new Error("Stems were returned but none could be downloaded into the mixer.");
      }
      setMaxDur(engine.maxDuration);
      setLoadedCount((c) => c + loaded.length);
      const firstStemCh = loaded[0].channel;
      const lastStemCh = Math.max(...loaded.map((l) => l.channel));
      setSelectedChannel(firstStemCh);
      setVisibleChannels((cur) => clamp(Math.max(cur, lastStemCh + 1), 8, TOTAL_CHANNELS));
      requestAnimationFrame(() => {
        try {
          window.dispatchEvent(new CustomEvent("elpm:jump-to-channel", { detail: { ch: firstStemCh } }));
        } catch (e) { void e; }
      });
      toast.dismiss(resumeToastId);
      toast.success(`Resumed extraction · loaded ${loaded.length} stems`, {
        description: loaded.map((l) => `${l.name}→CH${padCh(l.channel)}`).join(" · ") +
          " — drag to re-route if needed.",
        duration: 10000,
      });
      stemOk = true;
    } catch (e) {
      toast.dismiss(resumeToastId);
      clearInflightExtraction();
      if (e.name === "AbortError") {
        toast.message("Resume cancelled");
      } else {
        toast.error("Could not resume previous extraction", {
          description: e.message + " — start a fresh extraction when you're ready.",
          duration: 10000,
        });
      }
    } finally {
      clearInterval(stemBadgeTicker);
      clearBadge(stemBadgeId, stemOk, "Stems ready");
      setBusy(null);
      setStemProgress(null);
      stemAbortRef.current = null;
    }
  };

  useEffect(() => {
    if (resumeAttemptedRef.current) return;
    // Engine must be ready before we can route stems to channels.
    if (!engine || typeof engine.loadUrlToChannel !== "function") return;
    const inflight = readInflightExtraction();
    if (!inflight?.requestId) return;
    resumeAttemptedRef.current = true;
    void runResumedExtraction(inflight);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engine]);


  return {
    stemConfirm,
    setStemConfirm,
    stemProgress,
    handleStemExtract,
    runStemExtraction,
    handleCancelStems,
    // iter-252 — stems pack export.
    lastExtraction,
    downloadStemPack,
  };
}
